import React, { useCallback } from "react";
import { Entity } from "@common/types/entity";
import { useUser } from "../../hooks/useUser";
import { useEntities } from "../../hooks/useEntities";
import { completeOnboarding } from "../../lib/onboarding";
import { useFetchAsset } from "../../hooks/useFetchAsset";
import { useWorkspaceContext } from "../../state/workspaceContext";
import { usePartnerStackCookies } from "./usePartnerStackCookies";
import { OnboardingContainer } from "./components/OnboardingContainer";
import { RegistrationFlow } from "./flows/RegistrationFlow";
import { PrimaryFlow } from "./flows/PrimaryFlow";
import { OnboardingLayout } from "./layout/OnboardingLayout";
import { Outro } from "./steps/Outro/Outro";
import { PRODUCT_PREVIEW_URL } from "./constants";
import { InviteFlow } from "./flows/InviteFlow";

export const Onboarding: React.FC = () => {
    usePartnerStackCookies();
    const user = useUser();
    const { activeWorkspace } = useWorkspaceContext();
    const entitiesInWorkspace = useEntities({
        excludeEmptyMockEntities: true,
    });

    const handleFinishOnboarding = useCallback(async () => {
        if (!activeWorkspace) {
            return;
        }

        await completeOnboarding(activeWorkspace.id);
        window.location.replace("/");
    }, [activeWorkspace]);

    useFetchAsset(PRODUCT_PREVIEW_URL);

    if (!user) {
        return (
            <OnboardingContainer>
                <RegistrationFlow />
            </OnboardingContainer>
        );
    }

    const isOwnWorkspace = activeWorkspace?.primaryAdminUserId === user?.id;

    // Pick first business entity the user has access too (normal flow, normal invite)
    // Alternatively, pick a personal entity (odd case, where user has been invited to a workspace, but only to the personal entity)

    let primaryEntity: Entity | undefined;

    if (isOwnWorkspace) {
        primaryEntity = entitiesInWorkspace.find((e) => e.isBusiness);
    } else {
        primaryEntity =
            entitiesInWorkspace.find((e) => e.isBusiness) ??
            entitiesInWorkspace.find((e) => e.isPersonal);
    }

    if (
        activeWorkspace &&
        !activeWorkspace.onboardingCompletedAt &&
        primaryEntity
    ) {
        return (
            <OnboardingContainer>
                <PrimaryFlow
                    entity={primaryEntity}
                    onFinish={handleFinishOnboarding}
                />
            </OnboardingContainer>
        );
    }

    if (
        !!activeWorkspace?.onboardingCompletedAt &&
        !user.preferredName &&
        primaryEntity
    ) {
        return (
            <OnboardingContainer>
                <InviteFlow entity={primaryEntity} />
            </OnboardingContainer>
        );
    }

    return (
        <OnboardingContainer>
            <OnboardingLayout
                currentStep="outro"
                main={<Outro onFinished={handleFinishOnboarding} />}
            />
        </OnboardingContainer>
    );
};
